import { clearsEmptyProperties } from '@utils/functions/clearsEmptyProperties'
import { API_URLS } from '@services/apiRoutes'

import type {
  GetPreviewDataRequestProps,
  GetLastJustificationRequestProps,
  GetReturnPreviewQueryParamsProps,
  GetReturnPreviewResponseProps,
  PreviewDeadlineResponseProps,
  SaveDraftReturnPreviewRequestProps,
  UpdateDraftReturnPreviewRequestProps,
  SubmitReturnPreviewRequestProps,
  ReturnPreviewItemProps,
  ContasSemanaAtualResponseProps,
  BigNumbersFiltersRequestProps,
  BigNumbersFiltersResponseProps,
  ConsolidatedPreviewRequestProps,
  ConsolidedPreviewResponseProps,
  ConsolidatedPreviewFiltersRequestProps,
  ConsolidatedPreviewFiltersResponseProps,
  ConsolidatedPreviewDetailsRequestProps,
  ConsolidatedPreviewDetailsResponseProps,
  GetAdjustPreviewListRequestProps,
  AdjustPreviewListResponseProps,
  SaveNewAdjustPreviewRequestProps,
  ActionPreviewRequestProps,
  GetDeviationJustificationRequestProps,
  GetDeviationJustificationResponseProps,
  SaveDeviationJustificationRequestProps,
  SaveDeviationJustificationResponseProps,
  UpdateDeviationJustificationRequestProps,
  GetDeviationTypesRequestProps,
  DeviationTypeItemProps,
  SendReasonItemProps
} from './types'
import type { DeviationJustificationDataProps } from '@pages/Preview/components/modals/JustifyingDeviationsModal/form/types'

import * as apiFunction from '@services/apiFunctions'

export const deleteAdjustPreviewById = async (id: string): Promise<void> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.preview}/ajuste/${id}`,
    method: 'DELETE'
  })
}

export const getAdjustPreviewList = async (
  payload: GetAdjustPreviewListRequestProps
): Promise<AdjustPreviewListResponseProps[]> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/listar-ajustes`,
    method: 'POST',
    body: payload
  })
}

/**
 * GET /api/previa/contas-semana-atual
 * Retorna as contas da semana atual para o select do modal Ajustar Prévia.
 */
export const getContasSemanaAtual = async (
  referenceDate: string,
  branchCode: string
): Promise<ContasSemanaAtualResponseProps> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.preview}/contas-semana-atual`,
    method: 'GET',
    params: { referenceDate, branchCode }
  })
}

export const saveNewAdjustPreview = async (
  payload: SaveNewAdjustPreviewRequestProps
): Promise<void> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/novo-ajuste`,
    method: 'POST',
    body: payload
  })
}

export const getConsolidatedPreviewFilters = async (
  payload: ConsolidatedPreviewFiltersRequestProps
): Promise<ConsolidatedPreviewFiltersResponseProps> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/filtros-previa-consolidada`,
    method: 'POST',
    body
  })
}

export const getConsolidatedPreviewDetails = async (
  payload: ConsolidatedPreviewDetailsRequestProps
): Promise<ConsolidatedPreviewDetailsResponseProps> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/busca-previa-consolidada`,
    method: 'POST',
    body
  })
}

export const getBigNumbersFilters = async (
  payload: BigNumbersFiltersRequestProps
): Promise<BigNumbersFiltersResponseProps> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/filtros`,
    method: 'POST',
    body
  })
}

export const getConsolidatedPreview = async (
  payload: ConsolidatedPreviewRequestProps
): Promise<ConsolidedPreviewResponseProps> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/busca-painel-previa`,
    method: 'POST',
    body
  })
}


export const getPreviewData = async ({ bodyParams }: GetPreviewDataRequestProps) => {
  const body = clearsEmptyProperties(bodyParams)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/busca-painel-previa`,
    method: 'POST',
    body
  })
}

export const exportConsolidatedPreview = async (payload: ConsolidatedPreviewDetailsRequestProps) => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBodyAndResponseType({
    url: `${API_URLS.finapi.preview}/previas/export`,
    method: 'POST',
    body,
    responseType: 'blob'
  })
}

export const sendPreview = async (payload: ActionPreviewRequestProps) => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previaapproval}/send`,
    method: 'POST',
    body
  })
}

/**
 * GET /api/previaapproval/send-reasons
 * Lista os motivos de envio ativos, para o dropdown obrigatório a perfis ≠ GU.
 */
export const getSendReasons = async (): Promise<SendReasonItemProps[]> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.previaapproval}/send-reasons`,
    method: 'GET'
  })
}

export const validatePreview = async (payload: ActionPreviewRequestProps) => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previaapproval}/validate`,
    method: 'POST',
    body
  })
}

export const approvePreview = async (payload: ActionPreviewRequestProps) => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previaapproval}/approve`,
    method: 'POST',
    body
  })
}

export const adjustPreview = async (id: string) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/previaapproval/${id}/adjust`,
    method: 'POST'
  })
}

export const returnPreview = async (id: string) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/previaapproval/${id}/return`,
    method: 'POST'
  })
}

export const getReturnPreviewDetails = async (id: string) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/previaapproval/${id}/return`,
    method: 'GET'
  })
}

/**
 * GET /api/previaapproval/deadline
 * Retorna o prazo e mensagem do deadline para envio da prévia.
 */
export const getPreviewDeadline = async (): Promise<PreviewDeadlineResponseProps> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.previaapproval}/deadline`,
    method: 'GET'
  })
}

/**
 * GET /api/previaapproval/return
 * Busca estado da devolução (rascunho e/ou enviada) por branch, semana e data de referência.
 */
export const getReturnPreview = async (
  params: GetReturnPreviewQueryParamsProps
): Promise<GetReturnPreviewResponseProps> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.previaapproval}/return`,
    method: 'GET',
    params
  })
}

/**
 * POST /api/previaapproval/return/draft
 * Salva um item de devolução em rascunho (uma conta + justificativa).
 * Retorno: item criado/atualizado (mesmo formato de ReturnPreviewItemProps).
 */
export const postReturnDraft = async (
  payload: SaveDraftReturnPreviewRequestProps
): Promise<ReturnPreviewItemProps> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previaapproval}/return/draft`,
    method: 'POST',
    body
  })
}

/**
 * DELETE /api/previaapproval/return/draft/{id}
 * Remove um item de rascunho da devolução.
 */
export const deleteReturnDraft = async (id: string): Promise<void> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.previaapproval}/return/draft/${id}`,
    method: 'DELETE'
  })
}

/**
 * PUT /api/previaapproval/return/draft/{id}
 * Atualiza um item de rascunho da devolução.
 */
export const putReturnDraft = async (
  id: string,
  body: UpdateDraftReturnPreviewRequestProps
): Promise<ReturnPreviewItemProps> => {
  const payload = clearsEmptyProperties(body)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previaapproval}/return/draft/${id}`,
    method: 'PUT',
    body: payload
  })
}

/**
 * POST /api/previaapproval/return/submit
 * Envia a devolução (rascunho salvo vira devolução efetiva).
 */
export const postReturnSubmit = async (
  payload: SubmitReturnPreviewRequestProps
): Promise<void> => {
  const body = clearsEmptyProperties(payload)
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.previaapproval}/return/submit`,
    method: 'POST',
    body
  })
}

export const saveDraftReturnPreview = async (id: string, body: any) => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.preview}/previaapproval/${id}/return/draft`,
    method: 'POST',
    body
  })
}

/**
 * GET /api/deviationjustification
 * Busca a justificativa de desvio registrada para uma linha específica do grid.
 */
export const getDeviationJustification = async (
  params: GetDeviationJustificationRequestProps
): Promise<GetDeviationJustificationResponseProps | null> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: API_URLS.finapi.deviationJustification,
    method: 'GET',
    params
  })
}

/**
 * POST /api/deviationjustification
 * Salva a justificativa de desvio para uma linha do grid consolidado.
 */
export const saveDeviationJustification = async (
  body: SaveDeviationJustificationRequestProps
): Promise<SaveDeviationJustificationResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: API_URLS.finapi.deviationJustification,
    method: 'POST',
    body
  })
}

/**
 * PUT /api/deviationjustification/{id}
 * Atualiza uma justificativa de desvio já existente.
 */
export const updateDeviationJustification = async (
  id: number,
  body: UpdateDeviationJustificationRequestProps
): Promise<SaveDeviationJustificationResponseProps> => {
  return await apiFunction.apiRequestWithBody({
    url: `${API_URLS.finapi.deviationJustification}/${id}`,
    method: 'PUT',
    body
  })
}

/**
 * GET /api/deviationjustification/deviation-types
 * Busca tipos de desvio disponíveis por conta e polaridade (positivo/negativo).
 */
export const getDeviationTypes = async (
  params: GetDeviationTypesRequestProps
): Promise<DeviationTypeItemProps[]> => {
  return await apiFunction.apiRequestWithQueryParams({
    url: `${API_URLS.finapi.deviationJustification}/deviation-types`,
    method: 'GET',
    params
  })
}

/**
 * RF05: Busca a última justificativa registrada para pré-preenchimento
 * "pré-preencher automaticamente com a última justificativa registrada, mesmo que seja de semanas anteriores"
 */
export const getLastJustification = async ({
  economicGroup,
  profitCenter,
  accountCode
}: GetLastJustificationRequestProps): Promise<Partial<DeviationJustificationDataProps> | null> => {
  try {
    const response = await apiFunction.apiRequestWithBody({
      url: `${API_URLS.finapi.preview}/ultima-justificativa`,
      method: 'POST',
      body: {
        economicGroup,
        profitCenter,
        accountCode
      }
    })
    return response?.data || null
  } catch (error) {
    console.error('Erro ao buscar última justificativa:', error)
    return null
  }
}